import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, TrendingDown, TrendingUp, Info, DollarSign } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useValuationMethod } from "@/hooks/use-valuation-method";

interface CostImpactWarningProps {
  movementType: string;
  items: Array<{
    stockCode: string;
    description: string;
    quantity: number;
    unitPrice?: number;
    currentAvgCost?: number;
    currentQty?: number;
  }>;
}

export function CostImpactWarning({ movementType, items }: CostImpactWarningProps) {
  const { valuationMethod } = useValuationMethod();
  const getImpactDetails = () => {
    switch (movementType) {
      case "Purchase":
        return {
          icon: TrendingUp,
          variant: "default" as const,
          title: valuationMethod === 'fifo' 
            ? "Cost Impact: FIFO Cost Layer Will Be Created" 
            : "Cost Impact: Average Cost Will Be Recalculated",
          description: valuationMethod === 'fifo'
            ? "This purchase will create a new FIFO cost layer. When selling, the system will consume from the oldest cost layers first (FIFO method)."
            : "This purchase will update the average cost for the selected items based on the weighted average method.",
          impacts: items.map(item => {
            if (!item.unitPrice || !item.currentAvgCost) return null;
            const newQty = (item.currentQty || 0) + item.quantity;
            const newAvgCost = (((item.currentQty || 0) * item.currentAvgCost) + (item.quantity * item.unitPrice)) / newQty;
            const costChange = newAvgCost - item.currentAvgCost;
            const percentChange = (costChange / item.currentAvgCost) * 100;
            
            return {
              stockCode: item.stockCode,
              description: item.description,
              currentAvgCost: item.currentAvgCost,
              newAvgCost,
              costChange,
              percentChange,
              isIncrease: costChange > 0,
            };
          }).filter(Boolean),
        };

      case "Sales":
        return {
          icon: DollarSign,
          variant: "default" as const,
          title: "Cost Impact: COGS Will Be Calculated",
          description: valuationMethod === 'fifo'
            ? "Cost of Goods Sold (COGS) will be calculated using FIFO method - consuming from the oldest cost layers first."
            : "Cost of Goods Sold (COGS) will be calculated using the current average cost for each item.",
          impacts: items.map(item => {
            if (!item.unitPrice || !item.currentAvgCost) return null;
            const cogs = item.quantity * item.currentAvgCost;
            const revenue = item.quantity * item.unitPrice;
            const profit = revenue - cogs;
            const margin = (profit / revenue) * 100;
            
            return {
              stockCode: item.stockCode,
              description: item.description,
              cogs,
              revenue,
              profit,
              margin,
              isProfitable: profit > 0,
            };
          }).filter(Boolean),
        };

      case "Opening Balance":
        return {
          icon: Info,
          variant: "default" as const,
          title: "Cost Impact: Initial Average Cost Will Be Set",
          description: "This will set the initial average cost for the selected items. This is typically done once when starting to use the system.",
          impacts: items.map(item => {
            if (!item.unitPrice) return null;
            const totalValue = item.quantity * item.unitPrice;
            
            return {
              stockCode: item.stockCode,
              description: item.description,
              quantity: item.quantity,
              unitCost: item.unitPrice,
              totalValue,
            };
          }).filter(Boolean),
        };

      case "Write-off":
        return {
          icon: AlertTriangle,
          variant: "destructive" as const,
          title: "Cost Impact: Inventory Value Will Decrease",
          description: "The value of written-off items will be removed from inventory using the current average cost. Average cost will remain unchanged.",
          impacts: items.map(item => {
            if (!item.currentAvgCost) return null;
            const writeOffValue = item.quantity * item.currentAvgCost;
            
            return {
              stockCode: item.stockCode,
              description: item.description,
              quantity: item.quantity,
              avgCost: item.currentAvgCost,
              writeOffValue,
            };
          }).filter(Boolean),
        };

      case "Manual Adjustment":
        return {
          icon: Info,
          variant: "default" as const,
          title: "Cost Impact: Quantity Adjusted, Cost Unchanged",
          description: "This adjustment will change the quantity but will not affect the average cost per unit.",
          impacts: items.map(item => {
            if (!item.currentAvgCost) return null;
            const valueChange = item.quantity * item.currentAvgCost;
            
            return {
              stockCode: item.stockCode,
              description: item.description,
              quantity: item.quantity,
              avgCost: item.currentAvgCost,
              valueChange,
            };
          }).filter(Boolean),
        };

      case "Transfer Between Locations":
        return {
          icon: Info,
          variant: "default" as const,
          title: "Cost Impact: Average Cost Carried Over",
          description: "The average cost will be carried over to the new location. No cost recalculation occurs.",
          impacts: items.map(item => {
            if (!item.currentAvgCost) return null;
            const transferValue = item.quantity * item.currentAvgCost;
            
            return {
              stockCode: item.stockCode,
              description: item.description,
              quantity: item.quantity,
              avgCost: item.currentAvgCost,
              transferValue,
            };
          }).filter(Boolean),
        };

      default:
        return null;
    }
  };

  const impactDetails = getImpactDetails();
  if (!impactDetails || impactDetails.impacts.length === 0) return null;

  const Icon = impactDetails.icon;

  return (
    <Alert variant={impactDetails.variant} className="border-2">
      <Icon className="h-5 w-5" />
      <AlertTitle className="text-base font-semibold">{impactDetails.title}</AlertTitle>
      <AlertDescription className="mt-2 space-y-3">
        <p className="text-sm">{impactDetails.description}</p>
        
        <div className="space-y-2 mt-3">
          {movementType === "Purchase" && impactDetails.impacts.map((impact: any, index) => (
            <div key={index} className="bg-background/50 rounded-md p-3 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{impact.stockCode} - {impact.description}</span>
                <Badge variant={impact.isIncrease ? "default" : "secondary"}>
                  {impact.isIncrease ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                  {impact.percentChange > 0 ? "+" : ""}{impact.percentChange.toFixed(2)}%
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>Current Avg Cost: £{impact.currentAvgCost.toFixed(4)}</div>
                <div className="font-medium">New Avg Cost: £{impact.newAvgCost.toFixed(4)}</div>
                <div>Change: {impact.costChange > 0 ? "+" : ""}£{impact.costChange.toFixed(4)}</div>
              </div>
            </div>
          ))}

          {movementType === "Sales" && impactDetails.impacts.map((impact: any, index) => (
            <div key={index} className="bg-background/50 rounded-md p-3 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{impact.stockCode} - {impact.description}</span>
                <Badge variant={impact.isProfitable ? "default" : "destructive"}>
                  Margin: {impact.margin.toFixed(2)}%
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>COGS: £{impact.cogs.toFixed(2)}</div>
                <div>Revenue: £{impact.revenue.toFixed(2)}</div>
                <div className={`font-medium ${impact.isProfitable ? "text-green-600" : "text-red-600"}`}>
                  Profit: £{impact.profit.toFixed(2)}
                </div>
              </div>
            </div>
          ))}

          {movementType === "Opening Balance" && impactDetails.impacts.map((impact: any, index) => (
            <div key={index} className="bg-background/50 rounded-md p-3 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{impact.stockCode} - {impact.description}</span>
                <Badge variant="secondary">£{impact.totalValue.toFixed(2)}</Badge>
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>Quantity: {impact.quantity} units</div>
                <div>Unit Cost: £{impact.unitCost.toFixed(4)}</div>
                <div className="font-medium">Total Value: £{impact.totalValue.toFixed(2)}</div>
              </div>
            </div>
          ))}

          {movementType === "Write-off" && impactDetails.impacts.map((impact: any, index) => (
            <div key={index} className="bg-background/50 rounded-md p-3 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{impact.stockCode} - {impact.description}</span>
                <Badge variant="destructive">-£{impact.writeOffValue.toFixed(2)}</Badge>
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>Quantity Written Off: {impact.quantity} units</div>
                <div>Avg Cost: £{impact.avgCost.toFixed(4)}</div>
                <div className="font-medium text-red-600">Value Lost: £{impact.writeOffValue.toFixed(2)}</div>
              </div>
            </div>
          ))}

          {(movementType === "Manual Adjustment" || movementType === "Transfer Between Locations") && 
            impactDetails.impacts.map((impact: any, index) => (
              <div key={index} className="bg-background/50 rounded-md p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{impact.stockCode} - {impact.description}</span>
                  <Badge variant="secondary">
                    £{(impact.valueChange || impact.transferValue).toFixed(2)}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  <div>Quantity: {impact.quantity} units</div>
                  <div>Avg Cost: £{impact.avgCost.toFixed(4)} (unchanged)</div>
                  <div className="font-medium">
                    {movementType === "Transfer Between Locations" ? "Transfer" : "Adjustment"} Value: £{(impact.valueChange || impact.transferValue).toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </AlertDescription>
    </Alert>
  );
}
